// Pure helpers for check-ins ("I'm here"): the message presets and limits,
// the fix sent with one (a single browser geolocation reading, stored only on
// the check-in) and how they read in timelines.
import type { Schemas } from "@10twenty/api";

export const CHECK_IN_PRESETS = ["I'm here", "On my way", "Pick me up"] as const;
/** The server's limit, in characters (code points) after trimming. */
export const MESSAGE_MAX = 140;
/** How long to wait for a fix, and the oldest cached one to accept. */
export const GEOLOCATION_OPTIONS: PositionOptions = { enableHighAccuracy: true, timeout: 30_000, maximumAge: 60_000 };

/** The message to send: trimmed, or null when blank. */
export function checkInMessage(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
}

/** Characters the message would use, counted as the server counts them. */
export function messageLength(text: string): number {
  return [...text.trim()].length;
}

type Coordinates = Pick<
  GeolocationCoordinates,
  "latitude" | "longitude" | "accuracy" | "altitude" | "speed" | "heading"
>;

/**
 * The LiveLocation for a browser reading. Fields the browser leaves null (or
 * NaN, as for the heading of a phone standing still) are left out.
 */
export function pointFromPosition(position: { coords: Coordinates; timestamp: number }): Schemas["LiveLocation"] {
  const { coords } = position;
  const known = (value: number | null): value is number => value !== null && Number.isFinite(value);
  return {
    lat: coords.latitude,
    lon: coords.longitude,
    accuracy_m: Math.max(0, coords.accuracy),
    ...(known(coords.altitude) ? { altitude_m: coords.altitude } : {}),
    ...(known(coords.speed) && coords.speed >= 0 ? { speed_mps: coords.speed } : {}),
    ...(known(coords.heading) && coords.heading >= 0 && coords.heading < 360 ? { heading_deg: coords.heading } : {}),
    ts: new Date(position.timestamp).toISOString(),
  };
}

/** Why the browser gave no fix (`GeolocationPositionError.code`). */
export function geolocationErrorText(code: number): string {
  if (code === 1) return "This site isn't allowed to use your location. Allow it in the browser's site settings.";
  if (code === 3) return "Finding your location took too long. Try again.";
  return "Your location isn't available right now. Try again.";
}

/** "Checked in" or "Checked in: On my way", as timelines show it. */
export function checkInTitle(checkIn: Pick<Schemas["CheckIn"], "message">): string {
  return checkIn.message ? `Checked in: ${checkIn.message}` : "Checked in";
}
